import React, { useMemo, useCallback, useState } from 'react';
import { Box, VStack } from '@chakra-ui/react';
import { Tags, RefreshCw } from 'lucide-react';
import { 
  useGetConditionGroupsLookupsQuery,
  useConditionGroupFilters,
  useConditionGroupActions,
  ConditionGroupDetailsModal,
  ConditionGroupConditionsModal,
  createConditionGroupColumns,
  createConditionGroupActions,
  useBulkChangeStatusMutation,
  ConditionGroupLookup,
} from '@entities/condition-groups';
import { ConditionGroupCreateModal, ConditionGroupEditModal } from '@entities/condition-groups/components/ConditionGroupCreateEditModal';
import EntityTableSection from '@/components/section/EntityTableSection';
import type { BulkAction } from '@/components/section/EntityTableSection';
import PageHeader from '@/components/ui/PageHeader';
import SearchAndFiltersSection from '@/components/section/SearchAndFiltersSection';
import ErrorAlert from '@/components/ErrorAlert';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import ConfirmationModal from '@/components/modals/ConfirmationModal';
import { LOOKUP_STATUS_FILTER_OPTIONS, LOOKUP_STATUS_OPTIONS, getStatusBadgeProps } from '@/utils/lookupStatus';
import type { LookupStatus } from '@/utils/lookupStatus';
import { useMultiSelect } from '@/hooks/useMultiSelect';
import { useModal } from '@/hooks/useModal';

const ConditionGroupsLookupsPage: React.FC = () => {
  // Performance optimized with memoized hooks and callbacks
  const { filters, queryParams, handleFilterChange } = useConditionGroupFilters();
  const actions = useConditionGroupActions();
  
  const {
    data: groupsResponse,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useGetConditionGroupsLookupsQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });



  const groupsArray = useMemo(() => groupsResponse?.data || [], [groupsResponse?.data]);
  
  // Memoize filtered groups to prevent recalculation on every render
  const filteredGroups = useMemo(() => {
    return groupsArray.filter((group) => {
      const matchesSearch = group.name.toLowerCase().includes(filters.search.toLowerCase());
      const matchesStatus = filters.status === 'all' || group.status === filters.status;
      const matchesOrder = filters.order === 'all' || 
        (filters.order === 'ordered' && group.order) || 
        (filters.order === 'unordered' && !group.order);
      
      return matchesSearch && matchesStatus && matchesOrder;
    });
  }, [groupsArray, filters.search, filters.status, filters.order]);

  // Multi-select
  const multiSelect = useMultiSelect(filteredGroups);
  const [bulkChangeStatus, { isLoading: isBulkUpdating }] = useBulkChangeStatusMutation();
  const bulkConfirmModal = useModal();
  const [pendingBulkStatus, setPendingBulkStatus] = useState<LookupStatus | null>(null);

  const requestBulkStatusChange = useCallback((status: LookupStatus) => {
    setPendingBulkStatus(status);
    bulkConfirmModal.open();
  }, [bulkConfirmModal]);

  const handleBulkStatusConfirm = useCallback(async () => {
    if (!pendingBulkStatus) return;
    const ids = Array.from(multiSelect.selectedIds) as number[];
    try {
      await bulkChangeStatus({ ids, status: pendingBulkStatus }).unwrap();
      multiSelect.clearSelection();
      bulkConfirmModal.close();
      setPendingBulkStatus(null);
      refetch();
    } catch (err) {
      console.error('Bulk status update failed:', err);
    }
  }, [pendingBulkStatus, multiSelect.selectedIds, bulkChangeStatus, multiSelect.clearSelection, bulkConfirmModal, refetch]);

  const handleBulkConfirmClose = useCallback(() => {
    bulkConfirmModal.close();
    setPendingBulkStatus(null);
  }, [bulkConfirmModal]);

  const bulkActions: BulkAction[] = useMemo(() => {
    if (multiSelect.selectedCount === 0) return [];
    return [
      {
        key: 'changeStatus',
        label: 'Change Status',
        icon: RefreshCw,
        items: LOOKUP_STATUS_OPTIONS.map((opt) => ({
          key: opt.value,
          label: opt.label,
          onClick: () => requestBulkStatusChange(opt.value as LookupStatus),
        })),
      },
    ];
  }, [multiSelect.selectedCount, requestBulkStatusChange]);

  // Memoize search filters configuration
  const searchFilters = useMemo(() => [
    {
      key: 'status',
      label: 'All Status',
      value: filters.status,
      options: LOOKUP_STATUS_FILTER_OPTIONS,
      onChange: (value: string) => handleFilterChange('status', value),
    },
    {
      key: 'order',
      label: 'All Orders',
      value: filters.order,
      options: [
        { value: 'all', label: 'All' },
        { value: 'ordered', label: 'Has Order' },
        { value: 'unordered', label: 'No Order' },
      ],
      onChange: (value: string) => handleFilterChange('order', value),
    },
  ], [filters.status, filters.order, handleFilterChange]);

  // Memoize table columns to prevent recreation
  const tableColumns = useMemo(() => 
    createConditionGroupColumns(), []
  );

  // Memoize table actions
  const tableActions = useMemo(() => 
    createConditionGroupActions(actions.handleAction, actions.isDeleting),
    [actions.handleAction, actions.isDeleting]
  );

  // Memoize delete confirmation handler
  const handleDeleteConfirm = useCallback(async () => {
    const success = await actions.handleDelete();
    if (success) {
      refetch();
    }
  }, [actions.handleDelete, refetch]);

  // Memoize search change handler to prevent inline function re-renders
  const handleSearchChange = useCallback((value: string) => {
    handleFilterChange('search', value);
  }, [handleFilterChange]);

  // Memoize modal success handler to refresh data after create/update
  const handleModalSuccess = useCallback(() => {
    refetch();
  }, [refetch]);
  
  if (isLoading && !groupsArray.length) {
    return <LoadingSkeleton rows={5} />;
  }

  if (isError) {
    return (
      <ErrorAlert
        isError={true}
        title="Failed to load condition groups"
        message={error?.toString() || "An error occurred while loading data"}
        onRetry={refetch}
      />
    );
  }

  return (
    <Box maxW="full" mx="auto">
      <VStack gap={6} align="stretch">
        <PageHeader
          title="Seed Condition Groups"
          subtitle={`${filteredGroups.length} groups (${groupsArray.length} total)`}
          onAddNew={actions.handleCreateNew}
          addButtonText="Create Lookup Group"
          bulkActions={bulkActions}
          selectedCount={multiSelect.selectedCount}
          onRefresh={refetch}
          isRefreshing={isFetching}
        />

        <SearchAndFiltersSection
          searchValue={filters.search}
          searchPlaceholder="Search by group name..."
          onSearchChange={handleSearchChange}
          filters={searchFilters}
        />

        <EntityTableSection<ConditionGroupLookup>
          data={filteredGroups}
          columns={tableColumns}
          actions={tableActions}
          isLoading={isFetching}
          onRowClick={actions.handleRowClick}
          emptyState={{
            icon: Tags,
            title: "No condition groups found",
            description: "Create your first condition lookup group to get started",
          }}
          multiSelect={{
            selectedIds: multiSelect.selectedIds,
            onToggle: multiSelect.toggle,
            onToggleAll: multiSelect.toggleAll,
            isAllSelected: multiSelect.isAllSelected,
            getItemId: (item) => item.id,
          }}
        />

        {/* Hash-managed Modals - Each modal self-determines if it should render */}
        <ConditionGroupDetailsModal />

        <ConditionGroupCreateModal
          onSuccess={handleModalSuccess}
        />

        <ConditionGroupEditModal
          onSuccess={handleModalSuccess}
        />

        <ConditionGroupConditionsModal
          onSuccess={handleModalSuccess}
        />

        {/* Traditional modal for delete confirmation */}
        {actions.deleteConfirmModalProps.isOpen && (
          <ConfirmationModal
            {...actions.deleteConfirmModalProps}
            onConfirm={handleDeleteConfirm}
          />
        )}

        {bulkConfirmModal.isOpen && pendingBulkStatus && (
          <ConfirmationModal
            isOpen={bulkConfirmModal.isOpen}
            onClose={handleBulkConfirmClose}
            onConfirm={handleBulkStatusConfirm}
            title="Confirm Bulk Status Change"
            message={`Are you sure you want to change ${multiSelect.selectedCount} condition group${multiSelect.selectedCount > 1 ? 's' : ''} to "${getStatusBadgeProps(pendingBulkStatus).label}"?`}
            confirmText="Change Status"
            isLoading={isBulkUpdating}
            type="warning"
          />
        )}
      </VStack>
    </Box>
  );
};

export default ConditionGroupsLookupsPage; 